/**
 * Client ID Management
 *
 * Provides stable user identification for deterministic traffic allocation.
 *
 * STRICT MODE: PostHog distinct_id is REQUIRED
 * - PostHog distinct_id is the only source of clientId
 * - No UUID fallback generation (prevents race conditions)
 * - Throws error if PostHog is not available
 *
 * The client ID is used for deterministic bucketing in traffic control,
 * ensuring the same user gets consistent widget visibility decisions.
 */

import { getPostHog } from '../../analytics';
import { roseStorage } from '../../services/rose-storage';
import { logger } from '../core/logger';
import { maskId } from '../core/mask';

/**
 * Module-level flag to enforce PostHog requirement
 * When true, clientId generation requires PostHog to be available
 */
const isPostHogRequired = true;

/**
 * Generate a simple UUID v4
 */
function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Get or create a stable client ID from roseStorage
 *
 * In strict mode (isPostHogRequired=true), this function will throw an error
 * if no clientId exists in storage, as clientIds should only come from PostHog.
 *
 * Note: Persistence is handled by analytics providers (LocalStorageProvider, CookieProvider)
 * when PostHogProvider notifies the event bus with the clientId.
 */
function getRoseStorageClientId(): string {
    let clientId = roseStorage.getAnalyticsClientId();

    if (!clientId) {
        if (isPostHogRequired) {
            throw new Error(
                'PostHog not ready - clientId should not be generated without PostHog in strict mode'
            );
        }
        // Fallback UUID generation (only when PostHog is not required)
        clientId = generateUUID();
        logger.debug(
            '🆔 Generated new client ID (will be persisted by providers):',
            maskId(clientId)
        );
    }

    return clientId;
}

/**
 * Get PostHog distinct_id if available
 */
function getPostHogDistinctId(posthog: any): string | null {
    try {
        if (!posthog || !posthog.get_distinct_id) {
            return null;
        }

        const distinctId = posthog.get_distinct_id();

        if (distinctId && typeof distinctId === 'string') {
            logger.debug('🆔 Using PostHog distinct_id:', maskId(distinctId));
            return distinctId;
        }

        return null;
    } catch (error) {
        logger.debug('⚠️ Could not get PostHog distinct_id:', error);
        return null;
    }
}

/**
 * Get a stable client ID for traffic allocation
 *
 * STRICT MODE (isPostHogRequired=true):
 * - ONLY returns PostHog distinct_id
 * - Throws error if PostHog is not available or distinct_id is not set
 * - No UUID fallback (prevents race conditions)
 *
 * This ensures we always use PostHog's distinct_id for consistent cross-browser
 * and cross-session user identification.
 *
 * Note: This function is pure - it only returns the clientId.
 * Storage is handled by analytics providers (LocalStorageProvider, CookieProvider)
 * when the PostHogProvider notifies the event bus with the clientId.
 *
 * @param posthog Optional PostHog instance (if not provided, will be fetched automatically)
 * @returns Stable client ID string from PostHog distinct_id
 * @throws Error if PostHog is required but not available
 */
export function getStableClientId(posthog?: any): string {
    // If PostHog instance not provided, fetch it automatically
    const posthogInstance = posthog || getPostHog();

    // ALWAYS try PostHog first (best for cross-browser consistency)
    const posthogId = getPostHogDistinctId(posthogInstance);
    if (posthogId) {
        return posthogId;
    }

    // If PostHog is required and not available, throw error
    if (isPostHogRequired) {
        throw new Error(
            'PostHog distinct_id not available and PostHog is required. ' +
                'Ensure PostHog is initialized before calling getStableClientId().'
        );
    }

    // Fallback to roseStorage UUID (only when PostHog is not required)
    return getRoseStorageClientId();
}
